import type { FilterOperation } from '../OperationTypes.js'
import { NoConfig } from '../OperationTypes.js'
import { DateTime } from 'luxon'
import type { ValueListConfig } from './Config/ValueListConfig.js'
import { isValueListConfig } from './Config/ValueListConfig.js'
import { isDateRangeConfig } from './Config/DateRangeConfig.js'
import { isMinMaxTimeConfig } from './Config/MinMaxTimeConfig.js'
import { isSingleValueConfig } from './Config/SingleValueConfig.js'

const createFilterOperation = <TConfig, T extends string>(op: FilterOperation<T, TConfig>) => {
  return op
}

export const filterIsUnset = createFilterOperation({
  key: 'is-unset',
  type: 'filter',
  label: 'Is unset',
  applicableTo: ['enum', 'string', 'datetime'],
  apply: (samples, field) => {
    return samples.filter((s) => {
      const value = field.getter(s)
      return value === undefined || value === null
    })
  },
  config: NoConfig
})

export const filterIsNotUnset = createFilterOperation({
  key: 'is-not-unset',
  type: 'filter',
  label: 'Is not unset',
  applicableTo: ['enum', 'string', 'datetime'],
  apply: (samples, field) => {
    return samples.filter((s) => {
      const value = field.getter(s)
      return value !== undefined && value !== null
    })
  },
  config: NoConfig
})

export const filterContains = createFilterOperation({
  key: 'contains',
  type: 'filter',
  label: 'Contains',
  applicableTo: ['enum', 'string'],
  config: {
    defaultConfig: { value: '' },
    verifyConfig: isSingleValueConfig
  },
  apply: (samples, field, config) => {
    return samples.filter((s) => {
      const value = field.getter(s)
      if (value === undefined || value === null) return false
      return String(value).includes(config.value)
    })
  },
})

export const filterInList = createFilterOperation({
  key: 'filter-in',
  type: 'filter',
  label: 'Is in list',
  applicableTo: ['enum', 'string'],
  config: {
    defaultConfig: { values: [] } as ValueListConfig,
    verifyConfig: isValueListConfig
  },
  apply: (samples, field, config) => samples.filter((s) => config.values.includes(String(field.getter(s)))),
})

export const filterNotInList = createFilterOperation({
  key: 'filter-not-in',
  type: 'filter',
  label: 'Is not in list',
  applicableTo: ['enum', 'string'],
  config: {
    defaultConfig: { values: [] },
    verifyConfig: isValueListConfig
  },
  apply: (samples, field, config) => samples.filter((s) => !config.values.includes(String(field.getter(s)))),
})

export const filterDateRange = createFilterOperation({
  key: 'filter-date-range',
  type: 'filter',
  label: 'Date in range',
  applicableTo: ['datetime'],
  config: {
    defaultConfig: {},
    verifyConfig: isDateRangeConfig
  },
  apply: (samples, field, config) => {
    return samples.filter((s) => {
      const raw = field.getter(s)
      if (!raw) return false
      const date = DateTime.fromISO(String(raw))
      const from = config.from ? DateTime.fromISO(config.from) : DateTime.invalid('not set')
      const to = config.to ? DateTime.fromISO(config.to) : DateTime.invalid('not set')

      return (!from.isValid || date >= from) && (!to.isValid || date <= to)
    })
  },
})

export const filterDuration = createFilterOperation({
  key: 'filter-duration',
  type: 'filter',
  label: 'Duration Min/Max',
  applicableTo: ['datetime:duration'],
  config: {
    defaultConfig: { unit: 'days' },
    verifyConfig: isMinMaxTimeConfig
  },
  apply: (samples, field, config) => {
    const { min, max, unit } = config

    return samples.filter((sample) => {
      const value = field.getter(sample)
      if (!value || !Array.isArray(value) || value.length !== 2) return false

      const [startISO, endISO] = value
      const start = DateTime.fromISO(startISO)
      const end = DateTime.fromISO(endISO)

      if (!start.isValid || !end.isValid) return false

      const duration = end.diff(start, unit)[unit]

      if (duration === undefined) return false
      if (min !== undefined && duration < min) return false
      return !(max !== undefined && duration > max)
    })
  },
})
